<template>
  <VideoModal
    :isOpen="isVideoOpen"
    @closeModal="closeVideo"
    :title="$t('plus_promo_video_header')"
    itemKey="businessStudioPlus"
    id="Business Studio Plus"
  />
  <RemoveOffer :isOpen="isRemoveOfferOpen" @closeModal="closeRemoveOfferModal" />
  <CreateNewOffer
    :activeOffer="activeOffer"
    :hasCancellationDate="hasCancellationDate"
    :type="createOfferMode"
    :hasActiveOffer="hasActiveOffer"
    :isOpen="isCreateOfferOpen"
    :offer="selectedOffer"
    :icons="icons"
    :filteringCategories="filteringCategories"
    :templates="templates"
    :source="newOfferModalSource"
    @selectOffer="selectOfferFunc"
    @closeModal="closeCreateOfferOpen"
    @openSuggestionsModal="openSuggestionsModal"
  />

  <div
    v-if="!isPro || !isCancelledSubscription"
    class="tw:md:my-[58px] tw:border-t tw:mb-10 tw:pt-10 tw:mt-10 tw:md:border-none tw:md:pt-0"
    :data-testid="
      isStudioPlusEligible ? 'studio-plus-subscription-eligible-section' : 'studio-plus-subscription-active-section'
    "
  >
    <StudioPlusEligible v-if="isStudioPlusEligible" @openVideo="openVideo" />
    <StudioPlusActive
      v-else
      @openVideo="openVideo"
      @openReactivationFlow="openReactivationModal(selectedAccommodationId, 'home-page')"
    />
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import RemoveOffer from '@/components/hotelOffers/RemoveOffer.vue'
import CreateNewOffer from '@/components/hotelOffers/createOffer/CreateNewOffer.vue'
import StudioPlusEligible from '@/components/homePage/studioPlus/StudioPlusEligible.vue'
import StudioPlusActive from '@/components/homePage/studioPlus/StudioPlusActive.vue'
import VideoModal from '@/components/homePage/studioPlus/VideoModal.vue'

import icons from '@/constants/hotelOffersIcons.ts'

import { useHotelOffers } from '@/components/hotelOffers/useHotelOffers'
import { useActiveOffer, useOffersTemplates } from '@/components/hotelOffers/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { getDatesDifference } from '@/utils/hotelOffersUtils'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useProToPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useProToPlusReactivation'

import appAnalytics from '@/utils/tracking'
const route = useRoute()

const {
  closeRemoveOfferModal,
  selectOffer,
  closeCreateOfferOpen,
  isRemoveOfferOpen,
  createOfferMode,
  isCreateOfferOpen,
  selectedOffer,
  categoryNames,
  newOfferModalSource,
  suggestionsModalSource,
  isSuggestionsOpen,
} = useHotelOffers()
const { openReactivationModal, closeReactivationModal } = useStudioPlusReactivation()
const { openProToPlusModal } = useProToPlusReactivation()
const { hasActiveOffer, activeOffer } = useActiveOffer(selectedAccommodationId)
const { studioPlusCampaign, isPro, isCancelledSubscription } = useStudioPlusCampaign(selectedAccommodationId)
const { categories, templates } = useOffersTemplates()

const props = defineProps({
  isStudioPlusEligible: Boolean,
  isCancelled: Boolean,
})

const isVideoOpen = ref(false)
const openVideo = () => {
  appAnalytics.track(appAnalytics.events.SP_HOMEPAGE_PLUS_LEARN_MORE_BTN_CLICKED, {
    accommodationId: selectedAccommodationId.value,
  })
  isVideoOpen.value = true
}
const closeVideo = () => (isVideoOpen.value = false)

const hasCancellationDate = computed(() => {
  if (studioPlusCampaign.value?.endDate) {
    const studioCancellationDate = new Date(studioPlusCampaign.value.endDate.replace('@', 'T'))
    const activeOfferCancellationDate = new Date(activeOffer.value?.expirationDate?.replace('@', 'T'))
    const datesDifference = getDatesDifference(studioCancellationDate, activeOfferCancellationDate)
    return !(datesDifference === 0 || datesDifference === 1);
  } else {
    return activeOffer.value?.expirationDate !== null
  }
})
const filteringCategories = computed(() => {
  let allCategories = [{ value: 'all', label: categoryNames['all'] }]
  let newCategories =
    categories.value?.length > 0
      ? categories.value.map(category => {
          return {
            value: category,
            label: categoryNames[category],
          }
        })
      : []

  return [...allCategories, ...newCategories]
})

const selectOfferFunc = offerName => {
  selectOffer(offerName, templates)
}
const openSuggestionsModal = source => {
  appAnalytics.track(appAnalytics.events.HO_FEEDBACK_LINK_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    source: source === 'popup' ? 'Offer Setup Step 1' : 'Offers Grid Page',
  })
  suggestionsModalSource.value = source
  isSuggestionsOpen.value = true
}

const PRO_TO_PLUS_POPOVER_KEY = 'pro_to_plus_popover_shown'

onMounted(() => {
  if (route.query?.isReactivationOpen === 'true' && props.isCancelled) {
    openReactivationModal(selectedAccommodationId.value, 'home-page')
  } else {
    closeReactivationModal()
  }
})

// Auto-show PRO-to-Plus reactivation modal on first post-cancellation login.
// Watches campaign data because it loads async — onMounted would race the query.
watch(
  [studioPlusCampaign, selectedAccommodationId],
  () => {
    if (window.localStorage.getItem(PRO_TO_PLUS_POPOVER_KEY)) return
    if (!studioPlusCampaign.value || !selectedAccommodationId.value) return
    if (!isPro.value || !isCancelledSubscription.value) return

    window.localStorage.setItem(PRO_TO_PLUS_POPOVER_KEY, 'true')
    appAnalytics.track(appAnalytics.events.PRO_TO_PLUS_MODAL_OPENED, {
      item_id: selectedAccommodationId.value,
      source: 'post_login',
    })
    openProToPlusModal(selectedAccommodationId.value, 'post_login')
  },
  { immediate: true }
)
</script>
